import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useClient } from '../connect_api';

import { MachineService, MachineInfo, ProductService, ProductMap, TrackableItemState, Customer, EventType, Event } from '../api/api_pb';
import RelativeTime from '../components/RelativeTime';

import StateUpdateModal from '../components/StateUpdateModal';
import CurrencyFormatter from '../utils/currencyFormatter';
import BindingProductAndTackableItemModal from '../components/BindingProductAndTackableItemModal';
import { useGlobalContext } from '../GlobalContext';
import ConfirmModal from "../components/ConfirmModal";

const trackableItemStateMap: Record<number, string> = {
    0: 'Unspecified',
    1: 'Ready',
    2: 'In Use',
    3: 'Pending Inspection',
    4: 'Returned',
};

const LockerDetails: React.FC = () => {
    const { uuid } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { findMachineInfoByUuid: findMachineInfoById } = useGlobalContext();
    const machine: MachineInfo = location.state?.machine || findMachineInfoById(uuid!);
    const machineClient = useClient(MachineService);
    const productClient = useClient(ProductService);
    const [productMaps, setProductMaps] = useState<ProductMap[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [currentState, setCurrentState] = useState<TrackableItemState>(TrackableItemState.READY);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [productMap, setProductMap] = useState<ProductMap | null>(null);
    const { updateCustomers } = useGlobalContext();

    const [selectOpenItem,setSelectOpenItem] = useState<ProductMap|null>(null)

    const fetchProductMaps = useCallback(async (machineUuid: string) => {
        try {
            const timeoutId = setTimeout(() => {
                toast.dismiss();
                toast.error('Request timed out');
            }, 10000);

            try {
                const response = await machineClient.listMachineProductMaps({ machineUuid });
                clearTimeout(timeoutId);
                if (response.productMaps.length !== 0) {
                    setProductMaps(response.productMaps);
                }
            } catch (e) {
                clearTimeout(timeoutId);
                toast.dismiss();
                toast.error('Failed to fetch data');
                console.error('error', e);
            }
        } catch (err) {
            toast.dismiss();
            toast.error('Failed to fetch data');
            console.error('error', err);
        }
    }, [machineClient]);

    const handleUpdateState = async (newState: TrackableItemState) => {
        if (selectedItemId) {
            if (newState !== currentState) {
                try {
                    await productClient.updateTrackableItemState({
                        trackableItemUuid: selectedItemId,
                        state: newState,
                    });
                    fetchProductMaps(machine.uuid);
                    toast.dismiss();
                    toast.success('Update TrackableItemState Successfully');
                } catch (err) {
                    toast.dismiss();
                    toast.error('Failed to Update TrackableItem State');
                    console.error('Update TrackableItemState Error', err);
                }
            }
        }
    };

    const openDoor = (productMap:ProductMap) => {
        machineClient.openDoors({
            machineUuid: machine.uuid,
            productMapUuids: [productMap.uuid],
        }).then(res => {
            toast.dismiss();
            setSelectOpenItem(null)
            toast.success('Successfully sent door opening request');
        }).catch(err => {
            toast.dismiss();
            toast.error('Failed to send door opening request');
            console.error('OpenDoor Error', err);
        })
    };

    const handleShowleasesClick = (customer: Customer | undefined) => {
        if (customer) {
            updateCustomers(customer);
            navigate(`/show-lease/${customer.uuid}`);
        }
    };

    useEffect(() => {
        if (machine?.uuid) {
            fetchProductMaps(machine.uuid);
        }
    }, [machine, fetchProductMaps]);

    const handleRefreshTable = () => {
        fetchProductMaps(machine.uuid);
    };

    const handleSyncDoorsStatusBtn = () => {
        machineClient.sendEventToMachine({
            machineUuid: machine.uuid,
            event: { type: EventType.REPORT_DOORS_STATUS } as Event
        }).then(res => {
            setTimeout(() => {
                handleRefreshTable()
            }, 5000)
        }).catch(err => {
            toast.dismiss();
            toast.error('Failed to send sync doors status request');
        });
    };

    return (
        <div className="p-4">
            {/* Machine Details Table */}
            <table className="min-w-full table-auto border-collapse border border-gray-300">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 text-left px-4 py-2">UUID</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Name</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Address</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Description</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Image URL</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Pin Code</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Contact Number</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Created At</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Updated At</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Last Heartbeat At</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {machine ? (
                        <tr key={machine.uuid}>
                            <td className="border border-gray-300 px-4 py-2 relative group">
                                <div className={`absolute left-0 top-0 h-full w-[8px] ${machine.isOnline ? "bg-green-500" : "bg-gray-400"}`}></div>
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">{machine.isOnline ? "Online" : "Offline"}</span>
                                <span>{machine.uuid}</span>
                            </td>
                            <td className="border border-gray-300 px-4 py-2">{machine.name}</td>
                            <td className="border border-gray-300 px-4 py-2">{machine.address}</td>
                            <td className="border border-gray-300 px-4 py-2">{machine.description}</td>
                            <td className="border border-gray-300 px-4 py-2">
                                <img src={machine.imageUrl} alt={machine.name} className="w-20 h-20 object-contain" />
                            </td>
                            <td className="border border-gray-300 px-4 py-2">{machine.pinCode}</td>
                            <td className="border border-gray-300 px-4 py-2">{machine.contactNumber}</td>
                            <td className="border border-gray-300 px-4 py-2">
                                {machine.createdAt ? (
                                    <div className="text-sm">
                                        <div><RelativeTime timestamp={machine.createdAt} /></div>
                                    </div>
                                ) : ''}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                                {machine.updatedAt ? (
                                    <div className="text-sm">
                                        <div><RelativeTime timestamp={machine.updatedAt} /></div>
                                    </div>
                                ) : ''}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                                {machine.lastHeartbeatAt ? (
                                    <div className="text-sm">
                                        <div><RelativeTime timestamp={machine.lastHeartbeatAt} /></div>
                                    </div>
                                ) : ''}
                            </td>

                            <td className="border border-gray-300 px-4 py-2">
                                <button className="btn btn-primary mt-3" onClick={handleSyncDoorsStatusBtn}>Sync Doors Status</button>
                                <Link
                                    to={`/door-status-history/${machine.uuid}`}
                                    className="btn btn-info mt-3 ms-2"
                                >
                                    Door Status History
                                </Link>
                            </td>
                        </tr>
                    ) : (
                        <tr>
                            <td colSpan={11} className="border border-gray-300 px-4 py-2 text-center">No machine details available</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Product Maps Table */}
            <table className="min-w-full table-auto border-collapse border border-gray-300 mt-5">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 text-left px-4 py-2">UUID</th>
                        <th className="border border-gray-300 text-left px-4 py-2">MDB Item Number</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Door ID</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Door Status</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Product Name</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Product Image</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Product Price</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Trackable Item State</th>
                        <th className="border text-left border-gray-300 p-2">InUseCustomer</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Created At</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Updated At</th>
                        <th className="border border-gray-300 text-left px-4 py-2">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {productMaps.length === 0 ? (
                        <tr>
                            <td colSpan={10} className="border border-gray-300 px-4 py-2 text-center">No product maps available</td>
                        </tr>
                    ) : (
                        productMaps.map((map) => (
                            <tr key={map.uuid}>
                                <td className="border border-gray-300 px-4 py-2">{map.uuid}</td>
                                <td className="border border-gray-300 px-4 py-2">{map.mdbItemNumber.toString()}</td>
                                <td className="border border-gray-300 px-4 py-2">{map.doorIdDisplay}</td>
                                <td className="border border-gray-300 px-4 py-2">
                                    <p>status:{map.doorIsOpen ? <span className={'inline-block bg-blue-200 rounded-md px-1 py-1 text-sm font-semibold text-gray-700 mr-2'}>OPENED</span> : <span className={'inline-block bg-green-200 rounded-md px-1 py-1 text-sm font-semibold text-gray-700 mr-2'}>CLOSED</span>}</p>
                                    <p>updatedAt: {map.doorStatusLastUpdatedAt ? (
                                        <span className="text-sm">
                                            <span><RelativeTime timestamp={map.doorStatusLastUpdatedAt} /></span>
                                        </span>
                                    ) : ''}</p>
                                    {map.doorStatusLastUpdatedAt && (
                                        <Link
                                            to={`/door-status-history/${machine.uuid}/${map.mdbItemNumber}`}
                                            className="text-blue-600 hover:text-blue-800 underline text-sm"
                                        >
                                            Door Status History
                                        </Link>
                                    )}
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    {map.product ? (
                                        <>
                                            <div className="flex items-center gap-2">
                                                <span>{map.product.name}</span>
                                                {map.product.forSale && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        For Sale
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-sm text-gray-600">TrackableItem: {map.trackableItem?.name}</div>
                                        </>
                                    ) : ''}
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    {map.product && map.product.imageUrl ? (
                                        <img src={map.product.imageUrl} alt={map.product.name} className="w-20 h-20 object-contain" />
                                    ) : ''}
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    <CurrencyFormatter priceInCents={map.product ? map.product.priceInCents : 0} />
                                </td>
                                <td className="border border-gray-300 px-4 py-2">

                                    {map.trackableItem &&
                                        <>
                                            <div>{trackableItemStateMap[map.trackableItem?.state || 0]}</div><button
                                                className="btn btn-primary mt-3"
                                                onClick={() => {
                                                    setCurrentState(map.trackableItem?.state || TrackableItemState.READY);
                                                    setSelectedItemId(map.trackableItem?.uuid || null);
                                                    setShowModal(true);
                                                }}
                                            >
                                                Update State
                                            </button>
                                        </>}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-blue-500 hover:underline cursor-pointer">
                                    <Link
                                        to={`/show-lease/${map.trackableItem?.inUseCustomer?.uuid}`}
                                        onClick={() => handleShowleasesClick(map.trackableItem?.inUseCustomer)}
                                        className="text-blue-500 hover:underline"
                                    >
                                        {map.trackableItem?.inUseCustomer?.mobileNumber}
                                    </Link>
                                    {map.trackableItem?.inUseSinceAt ? (
                                        <div className="text-sm text-black">
                                            <div><RelativeTime timestamp={map.trackableItem.inUseSinceAt} /></div>
                                        </div>
                                    ) : ''}
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    {map.createdAt ? (
                                        <div className="text-sm">
                                            <div><RelativeTime timestamp={map.createdAt} /></div>
                                        </div>
                                    ) : ''}
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    {map.updatedAt ? (
                                        <div className="text-sm">
                                            <div><RelativeTime timestamp={map.updatedAt} /></div>
                                        </div>
                                    ) : ''}
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                    <div className="flex gap-3 mb-2">
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                                setSelectOpenItem(map);
                                            }}
                                        >
                                            Open Door
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                                setProductMap(map);
                                                setIsModalOpen(true);
                                            }}
                                        >
                                            Binding
                                        </button>
                                    </div>
                                    <div>
                                        <button
                                            className="text-blue-600 hover:text-blue-800 underline text-sm bg-transparent border-0 p-0 cursor-pointer"
                                            onClick={() => {
                                                navigate(`/cell-transactions/${machine.uuid}/${map.mdbItemNumber}`, {
                                                    state: { 
                                                        machine, 
                                                        productMap: map,
                                                        doorIdDisplay: map.doorIdDisplay 
                                                    }
                                                });
                                            }}
                                        >
                                            View Past Transactions
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {/* State Update Modal */}
            <StateUpdateModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                currentState={currentState}
                onUpdate={handleUpdateState}
            />

            <BindingProductAndTackableItemModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                refreshTable={handleRefreshTable}
                productMap={productMap}
            />

            <ConfirmModal
                isOpen={selectOpenItem != null}
                onClose={() => {
                setSelectOpenItem(null)
            }}
                onConfirm={() => {
                    if(selectOpenItem){
                        openDoor(selectOpenItem)
                    }else{
                        toast.error('Please select an item');
                    }
                }}
                title={"Please Confirm"}
                content={`Are you sure you want to open door ${selectOpenItem?.doorIdDisplay}?`}
            />
        </div>
    );
};

export default LockerDetails;
